import React from "react";
import { Dialog, Flex, Text, TextField, Button, Select, Badge, Separator, IconButton, Box } from "@radix-ui/themes";
import { ArrowRightIcon } from "@radix-ui/react-icons";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/solid";
import {
  DOTDIGITAL_CUSTOM_FIELD_MAPPINGS,
  normalizeDotdigitalFieldMappings,
} from "./dotdigitalFieldMappings";

const DOTDIGITAL_REGION_OPTIONS = [
  "https://r1-app.dotdigital.com/",
  "https://r2-app.dotdigital.com/",
  "https://r3-app.dotdigital.com/",
];

function getRegionCode(region) {
  const regionValue = String(region || "").trim();
  const match = /(?:https?:\/\/)?(r\d+)-app\.dotdigital\.com\/?/i.exec(regionValue);

  if (match?.[1]) {
    return match[1].toUpperCase();
  }

  return regionValue.replace(/^https?:\/\//, "").replace(/\/$/, "") || "Region";
}

export function AuthDotdigitalDialog({
  isOpen,
  onOpenChange,
  onCancel,
  onContinue,
  onAddAccount,
  onRemoveAccount,
  dotdigitalConfigs = [],
  formData,
  onFieldChange,
  onFieldMappingChange,
  fieldErrors,
  emailOptInOptions = [],
  contactSyncAudienceOptions = [],
  isAddAccountLoading = false,
  isContinueLoading = false,
  isContinueDisabled = false,
  apiError,
  title = "Connect Dotdigital",
  continueLabel = "Continue",
  showContinueStep = true,
  detailsDescription = "Validate each account first, then map only the locations you want in the next step.",
  queueDescription = "Validated accounts will be saved when you continue.",
  errorTitle = "Unable to complete request",
  showDataFieldMappings = false,
}) {
  const isBusy = isAddAccountLoading || isContinueLoading;
  const fieldMappings = normalizeDotdigitalFieldMappings(formData?.fieldMappings);

  return (
    <Dialog.Root open={isOpen} onOpenChange={onOpenChange}>
      <Dialog.Content
        maxWidth="680px"
        style={{
          height: "min(760px, calc(100vh - 96px))",
          padding: "28px",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Flex direction="column" gap="5" style={{ flex: "1 1 auto", minHeight: 0 }}>

          {/* Header of the Dialog */}
          <Flex direction="column" gap="3" style={{ flex: "0 0 auto" }}>
            <Flex direction="column" gap="2">
              <Dialog.Title>{title}</Dialog.Title>
              <Dialog.Description size="2" style={{ color: "var(--gray-a11)", maxWidth: "560px" }}>
                Add one or more Dotdigital accounts. Each account can have its own API user, password, and region.
              </Dialog.Description>
            </Flex>

            <Flex gap="2" wrap="wrap">
              <Badge size="2" variant="soft" color="blue">
                1. Add accounts
              </Badge>
              {showContinueStep ? (
                <Badge size="2" variant="soft" color="gray">
                  2. Continue to mapping
                </Badge>
              ) : null}
            </Flex>
          </Flex>

          <Box style={{ flex: "1 1 auto", minHeight: 0, overflowY: "auto", paddingRight: "var(--space-2)" }}>
            <Flex direction="column" gap="5">
              {/* Account details section */}
              <Flex p="4" direction="column" gap="4" style={{ width: "100%", border: "1px solid var(--gray-a5)",
                  borderRadius: "18px", background: "linear-gradient(180deg, var(--gray-1) 0%, var(--gray-2) 100%)",
                  boxShadow: "0 10px 30px rgba(15, 23, 42, 0.06)" }}>
                <Flex justify="between" align="start" gap="4" wrap="wrap">
                  <Flex direction="column" gap="1">
                    <Text size="3" weight="medium">
                      Account details
                    </Text>
                    <Text size="2" style={{ color: "var(--gray-a10)", maxWidth: "420px" }}>
                      {detailsDescription}
                    </Text>
                  </Flex>

                  <Button
                    onClick={() => onAddAccount()}
                    size="2"
                    variant="soft"
                    color="blue"
                    loading={isAddAccountLoading}
                    disabled={isBusy}
                  >
                    Add account
                  </Button>
                </Flex>

                <Separator size="4" />

                <Flex direction="column" gap="3" style={{ width: "100%" }}>
              <Flex direction="column" gap="1" align="start">
                <Text size="2" weight="medium">
                  API user
                </Text>
                <TextField.Root
                  value={formData.apiUser}
                  onChange={(event) => onFieldChange("apiUser", event.target.value)}
                  placeholder="Enter your Dotdigital API user"
                  style={{ width: "100%" }}
                  size="2"
                  variant="surface"
                />
                {fieldErrors.apiUser ? (
                  <Text size="1" color="red">
                    API user is required.
                  </Text>
                ) : null}
              </Flex>

              <Flex direction={{ initial: "column", sm: "row" }} gap="3" style={{ width: "100%" }}>
                <Flex direction="column" gap="1" align="start" style={{ width: "100%" }}>
                  <Text size="2" weight="medium">
                    API user password
                  </Text>
                  <TextField.Root
                    type="password"
                    value={formData.apiPassword}
                    onChange={(event) => onFieldChange("apiPassword", event.target.value)}
                    placeholder="Enter your Dotdigital API user password"
                    style={{ width: "100%" }}
                    size="2"
                    variant="surface"
                  />
                  {fieldErrors.apiPassword ? (
                    <Text size="1" color="red">
                      API user password is required.
                    </Text>
                  ) : null}
                </Flex>

                <Flex direction="column" gap="1" align="start" style={{ width: "100%" }}>
                  <Text size="2" weight="medium">
                    Account region
                  </Text>
                  <Select.Root value={formData.accountRegion} onValueChange={(value) => onFieldChange("accountRegion", value)}>
                    <Select.Trigger placeholder="Choose a Dotdigital region" style={{ width: "100%" }} />
                    <Select.Content position="popper">
                      {DOTDIGITAL_REGION_OPTIONS.map((regionUrl) => (
                        <Select.Item key={regionUrl} value={regionUrl}>
                          {regionUrl}
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Root>
                  {fieldErrors.accountRegion ? (
                    <Text size="1" color="red">
                      Account region is required.
                    </Text>
                  ) : null}
                </Flex>
              </Flex>

              <Flex direction="column" gap="1" align="start" style={{ width: "100%" }}>
                <Text size="2" weight="medium">
                  Email opt-in type
                </Text>
                <Select.Root value={formData.emailOptInType || "single"} onValueChange={(value) => onFieldChange("emailOptInType", value)}>
                  <Select.Trigger placeholder="Choose an opt-in type" style={{ width: "100%" }} />
                  <Select.Content position="popper">
                    {(emailOptInOptions.length > 0 ? emailOptInOptions : [{ value: "single", label: "Single opt-in" }]).map((option) => (
                      <Select.Item key={option.value} value={option.value}>
                        {option.label}
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Root>
              </Flex>

              <Flex direction="column" gap="1" align="start" style={{ width: "100%" }}>
                <Text size="2" weight="medium">
                  Contact sync audience
                </Text>
                <Select.Root
                  value={formData.syncOnlySubscribedContacts === false ? "all" : "subscribed"}
                  onValueChange={(value) => onFieldChange("syncOnlySubscribedContacts", value !== "all")}
                >
                  <Select.Trigger placeholder="Choose which guests to sync" style={{ width: "100%" }} />
                  <Select.Content position="popper">
                    {(contactSyncAudienceOptions.length > 0
                      ? contactSyncAudienceOptions
                      : [{ value: "subscribed", label: "Only opted-in guests" }]
                    ).map((option) => (
                      <Select.Item key={option.value} value={option.value}>
                        {option.label}
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Root>
              </Flex>

              {showDataFieldMappings ? (
                <Flex direction="column" gap="2" align="start" style={{ width: "100%" }}>
                  <Flex direction="column" gap="1">
                    <Text size="2" weight="medium">
                      Data fields
                    </Text>
                    <Text size="1" color="gray">
                      Map b connect fields to the matching Dotdigital data field names for this account.
                    </Text>
                  </Flex>

                  <Flex direction="row" justify="between" align="center" style={{ width: "100%" }}>
                    <Text size="2" weight="medium" style={{ width: "46%" }}>b connect</Text>
                    <Text size="2" weight="medium" style={{ width: "46%" }}>Dotdigital data field</Text>
                  </Flex>

                  {DOTDIGITAL_CUSTOM_FIELD_MAPPINGS.map((field) => (
                    <Flex key={field.source} direction="row" justify="between" align="center" style={{ width: "100%" }}>
                      <Flex style={{ width: "46%", minWidth: 0 }}>
                        <TextField.Root value={field.label} disabled style={{ width: "100%", backgroundColor: "var(--gray-3)" }} />
                      </Flex>
                      <Flex align="center" px="2">
                        <IconButton size="2" variant="ghost" color="gray" style={{ pointerEvents: "none" }}>
                          <ArrowRightIcon width="16" height="16" />
                        </IconButton>
                      </Flex>
                      <Flex style={{ width: "46%", minWidth: 0 }}>
                        <TextField.Root
                          value={fieldMappings[field.source] || ""}
                          onChange={(event) => onFieldMappingChange?.(field.source, event.target.value)}
                          placeholder={field.defaultDotdigitalField}
                          style={{ width: "100%" }}
                          size="2"
                          variant="surface"
                        />
                      </Flex>
                    </Flex>
                  ))}
                </Flex>
              ) : null}

              {apiError ? (
                <Flex
                  direction="column"
                  gap="1"
                  style={{
                    padding: "10px 12px",
                    borderRadius: "12px",
                    border: "1px solid var(--red-a5)",
                    background: "var(--red-2)",
                  }}
                >
                  <Text size="1" weight="medium" color="red">
                    {errorTitle}
                  </Text>
                  <Text size="1" color="red">
                    {apiError}
                  </Text>
                </Flex>
              ) : null}
                </Flex>
              </Flex>

              {/* Accounts ready to connect */}
              <Flex p="4" direction="column" gap="3" style={{ width: "100%", border: "1px solid var(--gray-a5)",
                  borderRadius: "18px", background: "var(--gray-2)" }}>
                <Flex justify="between" align="center">
                  <Flex direction="column" gap="1">
                    <Text size="3" weight="medium">
                      Accounts ready to connect
                    </Text>
                    <Text size="2" style={{ color: "var(--gray-a10)" }}>
                      {queueDescription}
                    </Text>
                  </Flex>

                  <Badge size="2" variant="soft" color="gray">
                    {dotdigitalConfigs.length}
                  </Badge>
                </Flex>

                <Flex direction="column" gap="2" style={{ width: "100%", maxHeight: "220px", overflowY: "auto" }}>
                  {dotdigitalConfigs.length > 0 ? (
                    dotdigitalConfigs.map((config) => (
                      <Flex
                        key={config.localId || `${config.apiUser}-${config.accountRegion}`}
                        direction="row"
                        justify="between"
                        align="center"
                        gap="3"
                        style={{
                          padding: "12px 14px",
                          border: "1px solid var(--gray-a6)",
                          borderRadius: "14px",
                          background: "#FFFFFF",
                        }}
                      >
                        <Flex direction="column" gap="1" style={{ minWidth: 0 }}>
                          <Text size="2" weight="medium" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {config.apiUser}
                          </Text>
                          <Flex gap="2" align="center" wrap="wrap">
                            <Badge size="1" variant="soft" color="blue">
                              {getRegionCode(config.accountRegion)}
                            </Badge>
                            <Badge size="1" variant="soft" color="gray">
                              {config.emailOptInType === "verifiedDouble" ? "Verified double" : "Single"}
                            </Badge>
                            <Badge size="1" variant="soft" color="gray">
                              {config.syncOnlySubscribedContacts === false ? "All guests" : "Opted-in only"}
                            </Badge>
                            <Text size="1" style={{ color: "var(--gray-a10)" }}>
                              Validated
                            </Text>
                          </Flex>
                        </Flex>

                        <Button size="1" variant="soft" color="red" onClick={() => onRemoveAccount(config.localId)} disabled={isBusy}>
                          Remove
                        </Button>
                      </Flex>
                    ))
                  ) : (
                    <Flex
                      align="center"
                      justify="center"
                      style={{
                        minHeight: "96px",
                        padding: "16px",
                        border: "1px dashed var(--gray-a6)",
                        borderRadius: "14px",
                        background: "rgba(255, 255, 255, 0.65)",
                      }}
                    >
                      <Text size="2" style={{ color: "var(--gray-a10)" }}>
                        No Dotdigital accounts added yet.
                      </Text>
                    </Flex>
                  )}
                </Flex>
              </Flex>
            </Flex>
          </Box>

          {/* Action buttons */}
          <Flex
            justify="between"
            align="center"
            gap="3"
            wrap="wrap"
            style={{
              width: "100%",
              paddingTop: "var(--space-3)",
              borderTop: "1px solid var(--gray-a5)",
              flex: "0 0 auto",
            }}
          >
            <Button size="2" variant="ghost" color="gray"
              disabled={isBusy}
              onClick={() => window.open(formData.accountRegion || DOTDIGITAL_REGION_OPTIONS[0], "_blank")}>
              <Flex display="flex" align="center" gap="2">
                Open Dotdigital Region
                <ArrowTopRightOnSquareIcon style={{ width: "16px", height: "16px" }} />
              </Flex>
            </Button>

            <Flex display="flex" gap="3" justify="end">
              <Dialog.Close>
                <Button onClick={onCancel} size="2" variant="soft" color="gray" disabled={isBusy}>
                  Cancel
                </Button>
              </Dialog.Close>
              <Button
                onClick={onContinue}
                size="2"
                variant="solid"
                loading={isContinueLoading}
                disabled={isContinueDisabled || isBusy}
              >
                {continueLabel}
              </Button>
            </Flex>
          </Flex>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}
